import { Trans, useLingui } from "@lingui/react/macro";
import { BOT_NAME_MAX_LENGTH } from "@orbin/contracts";
import {
  BOT_AVATAR_COLORS,
  BotAvatar,
  BotColorPicker,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@orbin/ui-web";
import { useRef, useState } from "react";
import { useBilling } from "../../components/SubscriptionGate";
import { planAgentsLimit } from "../../lib/billing-errors";
import { errorText } from "../../lib/user-error";

function pick<T>(options: readonly T[]): T {
  return options[Math.floor(Math.random() * options.length)]!;
}

export interface CreateBotDialogInput {
  name: string;
  color: string;
}

/** Mounted only while open; the parent unmounts it from `onCancel` or after `onCreate`. */
export function CreateBotDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (input: CreateBotDialogInput) => Promise<void>;
}) {
  const { t } = useLingui();
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(() => pick(BOT_AVATAR_COLORS));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState<{ maxAgents: number } | null>(null);
  const { showPlans } = useBilling();
  const trimmed = name.trim();

  async function create() {
    if (!trimmed || submitting) return;
    setError(null);
    setLimit(null);
    setSubmitting(true);
    try {
      await onCreate({ name: trimmed, color });
    } catch (err) {
      const full = planAgentsLimit(err);
      if (full) setLimit(full);
      else setError(errorText(err, t`Could not create bot`));
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        initialFocus={nameRef}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md"
        data-testid="create-bot-dialog"
      >
        <form
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-center">
              <Trans>New bot</Trans>
            </DialogTitle>
          </DialogHeader>
          <BotAvatar
            color={color}
            avatarSet="prea"
            identity={name || color}
            size={56}
            className="self-center"
          />
          <Input
            ref={nameRef}
            aria-label={t`Name`}
            placeholder={t`Name your Bot`}
            value={name}
            maxLength={BOT_NAME_MAX_LENGTH}
            onChange={(event) => setName(event.target.value)}
            className="text-center"
          />
          {limit ? (
            <div
              role="alert"
              data-testid="create-bot-limit"
              className="flex items-center justify-between gap-3 text-[13px] text-destructive"
            >
              <span>
                {limit.maxAgents > 0
                  ? t`Your plan allows up to ${limit.maxAgents} bots.`
                  : t`Your plan has no room for another bot.`}
              </span>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => {
                  onCancel();
                  showPlans();
                }}
              >
                <Trans>See plans</Trans>
              </Button>
            </div>
          ) : null}
          {error ? (
            <p role="alert" data-testid="create-bot-error" className="text-[13px] text-destructive">
              {error}
            </p>
          ) : null}
          <BotColorPicker value={color} label={(hex) => `Color ${hex}`} onChange={setColor} />
          <DialogFooter>
            <Button type="button" variant="outline" disabled={submitting} onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={!trimmed || submitting}>
              <Trans>Create</Trans>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
